import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { colors } from "../utils/colors";
import * as TypographyStyles from './TypographyStyles';
import { ReactSVG } from 'react-svg';

const Container = styled('div')`
  display: flex;
  padding: 64px 32px 64px 32px;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  align-self: stretch;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  
  svg {
    width: 60px;
    height: auto;
  }
  
  @media (min-width: 768px) {
    svg {
      width: 80px;
    }
  }
`

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  align-self: stretch;
`


// Title styling
const Title = styled(TypographyStyles.PageTitle)`
color: ${colors.grey800};
text-align: center;
leading-trim: both;
text-edge: cap;
`;

// Subtitle styling
const Subtitle = styled(TypographyStyles.BodyXLRegular)`
  color: ${colors.grey800};
  text-align: center;
  leading-trim: both;
  text-edge: cap;
`;

// Message styling
const MessageText = styled(TypographyStyles.BodyMRegular)`
  color: ${colors.grey600};
  text-align: center;
  leading-trim: both;
  text-edge: cap;
`;

// Props interface with type property
interface MessageProps {
  type: 'loading' | 'error' | 'timeout' | 'no-journeys';
  message: string | null;
}

// Define titles and subtitles based on the type
const getTitleAndSubtitle = (type: 'loading' | 'error' | 'timeout' | 'no-journeys', customMessage?: string | null, t?: (key: string) => string) => {
  const translate = t || ((key: string) => key);
  switch (type) {
    case 'loading':
      return {
        title: translate('message.searching'),
        subtitle: '', // Subtitle will be managed dynamically
      };
    case 'no-journeys':
    case 'error':
    case 'timeout':
    default:
      return {
        title: translate('message.noJourneysFound'),
        subtitle: translate('message.noJourneysSubtitle'),
      };
  }
};

export const Message: React.FC<MessageProps> = ({ type, message }) => {
  const { t } = useTranslation();
  
  // Parse message if it contains both user-friendly and original error (format: "userFriendly|originalError")
  const [userFriendlyMessage, originalErrorMessage] = message?.split('|') || [null, null];
  
  // Always use the default subtitle from getTitleAndSubtitle, ignore userFriendlyMessage for subtitle
  const { title, subtitle: defaultSubtitle } = getTitleAndSubtitle(type, userFriendlyMessage || message, t);
  const subtitleToUse = defaultSubtitle;
  
  const [ subtitle, setSubtitle] = useState<string>(subtitleToUse);

  useEffect(() => {
    let timer1: NodeJS.Timeout | null = null;
    let timer2: NodeJS.Timeout | null = null;

    if (type === 'loading') {
      // Initially, no subtitle
      setSubtitle('');
      // Set subtitle after 4 seconds
      timer1 = setTimeout(() => {
        setSubtitle(t('message.holdOn'));
      }, 4000);

      // Update subtitle after 8 seconds
      timer2 = setTimeout(() => {
        setSubtitle(t('message.processingData'));
      }, 8000);
    } else {
      // Immediately set subtitle for error/no-journeys/timeout types
      setSubtitle(subtitleToUse);
    }

    // Cleanup timeouts on unmount or when type changes
    return () => {
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
    };
  }, [type, subtitleToUse, t]);

  // Show original error message in smallest text if available, otherwise show message as-is
  const displayMessage = originalErrorMessage || (type === 'no-journeys' ? null : message);

  // Show icon for error and no-journeys types, but not for loading or timeout
  const showIcon = type === 'error' || type === 'no-journeys';

  return (
    <Container>
      {showIcon && (
        <IconContainer>
          <ReactSVG
            src="/images/sad chuuchuu icon.svg"
            beforeInjection={(svg) => {
              // Set fill color on all path elements
              const paths = svg.querySelectorAll('path');
              paths.forEach((path) => {
                path.setAttribute('fill', colors.key);
              });
            }}
          />
        </IconContainer>
      )}
      <Title>{title}</Title>
      <Body>
        <Subtitle>{subtitle}</Subtitle>
        {displayMessage && <MessageText>{displayMessage}</MessageText>}
      </Body>
    </Container>
  );
};

